<template>
  <model-crud
    :model="model"
    :fields="fields"
    :include="['companyGroup']"
    :atferSave="atferSave"
    :form-order="formOrder"
  >
    <template slot="recordingsSize" slot-scope="row">
      {{ $format.ratio(row.item.recordingsSize, row.item.quota * (1024 * 1024)) }}
    </template>
  </model-crud>
</template>

<script>
import ModelCrud from '@/components/ModelCrud'
import Company from '@/models/Company'
import CompanyGroup from '@/models/CompanyGroup'
import User from '@/models/User'
import PasswordInput from '@/components/PasswordInput'

export default {
  data () {
    let self = this
    let user = this.$store.auth.user

    let fields = {
      name: {
        label: this.$t('headers.name'),
        required: true
      },
      description: {
        label: this.$t('headers.description')
      },
      note: {
        label: this.$t('headers.note'),
        fill: true,
        multiLine: true,
        displayLength: 20
      },
      ico: {
        label: this.$t('headers.ico'),
        fill: true,
        required: true
      },
      sipId: {
        label: this.$t('headers.sipId'),
        fill: true,
        list: false
      },
      customLogo: {
        label: this.$t('headers.customLogo'),
        hint: this.$t('headers.customLogoHint'),
        fill: true,
        list: false,
        filterable: false
      },
      customCss: {
        label: this.$t('headers.customCss'),
        hint: this.$t('headers.customCssHint'),
        fill: true,
        list: false,
        filterable: false
      },
      quota: {
        label: this.$t('headers.quota'),
        list: false,
        fill: true,
        hint: this.$t('inMb'),
        required: true,
        preProcess: (value) => Math.round((value / (1024 * 1024)) * 100) / 100,
        postProcess: (value) => value * (1024 * 1024),
        rules: [
          (value) => value && value < 0 ? this.$t('errors.positiveNumbersOnly') : true
        ]
      },
      maxChannels: {
        label: this.$t('headers.maxChannels'),
        list: false,
        fill: true,
        required: true,
        rules: [
          (value) => value && value < 0 ? this.$t('errors.positiveNumbersOnly') : true
        ]
      },
      companyGroup: {
        label: this.$t('headers.companyGroup'),
        type: 'reference',
        model: CompanyGroup,
        required: true,
        fill: true
      },
      passwordComplexity: {
        label: this.$t('headers.passwordComplexity'),
        type: 'enum',
        values: Company.enums.passwordComplexity,
        list: false,
        fill: true,
        required: true,
        default: (user.company && user.company.companyGroup && user.company.companyGroup.passwordComplexity) || '3',
        rules: [
          function (value) {
            if (this.companyGroup) {
              if (value < this.companyGroup.passwordComplexity) {
                return self.$t('errors.passwordComplexityIsLessThanCompanyGroup')
              }
            }
            return true
          }
        ]
      },
      passwordExpiry: {
        label: this.$t('headers.passwordExpiry'),
        type: 'number',
        list: false,
        fill: true,
        hint: this.$t('inDays'),
        default: (user.company && user.company.companyGroup && user.company.companyGroup.passwordExpiry) || 356,
        required: true,
        rules: [
          function (value) {
            if (this.companyGroup && this.companyGroup.passwordExpiry) {
              if (value > this.companyGroup.passwordExpiry) {
                return self.$t('errors.passwordExpiryIsLessThanCompanyGroup', [this.companyGroup.passwordExpiry])
              }
            }
            return true
          }
        ]
      },
      recordingsSize: {
        label: this.$t('headers.recordingsSize'),
        editable: false,
        filterable: false
      },
      requireEncryption: {
        label: this.$t('headers.requireEncryption'),
        type: 'bool',
        fill: true,
        list: false
      },
      recordingsConfirmations: {
        label: this.$t('headers.recordingsConfirmations'),
        type: 'bool',
        fill: true,
        list: false
      },
      allowRecordingDeletion: {
        label: this.$t('headers.allowRecordingDeletion'),
        type: 'bool',
        fill: true,
        list: false
      },
      enabled: {
        label: this.$t('headers.enabled'),
        type: 'bool',
        default: true,
        fill: true
      },
      adminCreate: {
        label: this.$t('headers.createAdmin'),
        type: 'bool',
        createOnly: true,
        list: false,
        filterable: false,
        fill: true,
        default: true
      },
      adminSendInfo: {
        label: this.$t('headers.sendInfo'),
        type: 'bool',
        createOnly: true,
        list: false,
        filterable: false,
        fill: true,
        requires: 'adminCreate',
        default: true
      },
      adminUsername: {
        label: this.$t('headers.username'),
        createOnly: true,
        list: false,
        filterable: false,
        fill: true,
        requires: 'adminCreate',
        required: true,
        rules: [
          (value) => value && value.length < 3 ? this.$t('errors.mustBeLonger', [3]) : true
        ]
      },
      adminName: {
        label: this.$t('headers.personName'),
        createOnly: true,
        list: false,
        filterable: false,
        requires: 'adminCreate',
        rules: [
          (value) => value && value.length < 3 ? this.$t('errors.mustBeLonger', [3]) : true
        ]
      },
      adminSurname: {
        label: this.$t('headers.surname'),
        createOnly: true,
        list: false,
        filterable: false,
        requires: 'adminCreate',
        rules: [
          (value) => value && value.length < 3 ? this.$t('errors.mustBeLonger', [3]) : true
        ]
      },
      adminEmail: {
        label: this.$t('headers.email'),
        createOnly: true,
        list: false,
        filterable: false,
        requires: 'adminCreate',
        fill: true,
        required: true
      },
      adminPassword: {
        label: this.$t('headers.password'),
        type: 'custom',
        createOnly: true,
        list: false,
        filterable: false,
        requires: 'adminCreate',
        required: true,
        input: PasswordInput
      },
      adminPassword2: {
        label: this.$t('headers.passwordAgain'),
        type: 'custom',
        createOnly: true,
        list: false,
        filterable: false,
        requires: 'adminCreate',
        required: true,
        input: PasswordInput,
        otherPassword: 'adminPassword'
      }
    }

    return {
      formOrder: Object.keys(fields),
      model: Company,
      fields
    }
  },
  methods: {
    atferSave (result, item, fields) {
      if (item.adminCreate) {
        let admin = {
          username: item.adminUsername,
          name: item.adminName,
          surname: item.adminSurname,
          email: item.adminEmail,
          role: 'COMPANY_ADMIN',
          company: result
        }
        let user

        return User.create(admin)
          .then(created => {
            user = created
            user.password = item.adminPassword
            return User.setPassword(user)
          })
          .then(() => {
            if (item.adminSendInfo) {
              return User.sendInfo(user)
            }
          })
      }
      return Promise.resolve()
    }
  },
  components: {
    ModelCrud
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// Companies.vue?61c93c94